import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BADGES = ["Top Prospect", "Elite", "À suivre"];
const empty = { nom: "", poste: "", age: "", club: "", nationalite: "", badge: "Top Prospect", image: "", vitesse: 80, technique: 80, physique: 80 };

export default function AdminPlayers() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/players`).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image) {
      toast.error("Téléversez une photo du joueur");
      return;
    }
    const payload = {
      nom: form.nom, poste: form.poste, age: parseInt(form.age, 10), club: form.club,
      nationalite: form.nationalite, badge: form.badge, image: form.image,
      stats: { vitesse: +form.vitesse, technique: +form.technique, physique: +form.physique },
    };
    try {
      if (editing) await axios.put(`${API}/admin/players/${editing}`, payload, { withCredentials: true });
      else await axios.post(`${API}/admin/players`, payload, { withCredentials: true });
      toast.success(editing ? "Joueur mis à jour" : "Joueur ajouté");
      setForm(empty); setEditing(null); setShowForm(false);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const edit = (p) => {
    setForm({ nom: p.nom, poste: p.poste, age: p.age, club: p.club, nationalite: p.nationalite, badge: p.badge, image: p.image, ...p.stats });
    setEditing(p.id); setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce joueur ?")) return;
    await axios.delete(`${API}/admin/players/${id}`, { withCredentials: true });
    toast.success("Joueur supprimé"); load();
  };

  return (
    <div data-testid="admin-players">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#71717A]">{items.length} joueur(s) affichés sur le site</p>
        <button
          data-testid="player-add-button"
          onClick={() => { setForm(empty); setEditing(null); setShowForm(!showForm); }}
          className="flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-black transition-colors duration-300"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Fermer" : "Ajouter"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="player-form" className="border border-[#27272A] bg-[#121214] p-8 mb-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <input data-testid="player-input-nom" required placeholder="Nom *" value={form.nom} onChange={set("nom")} className="input-editorial" />
          <input data-testid="player-input-poste" required placeholder="Poste *" value={form.poste} onChange={set("poste")} className="input-editorial" />
          <input data-testid="player-input-age" required type="number" min="14" max="45" placeholder="Âge *" value={form.age} onChange={set("age")} className="input-editorial" />
          <input data-testid="player-input-club" required placeholder="Club *" value={form.club} onChange={set("club")} className="input-editorial" />
          <input data-testid="player-input-nationalite" required placeholder="Nationalité *" value={form.nationalite} onChange={set("nationalite")} className="input-editorial" />
          <select data-testid="player-select-badge" value={form.badge} onChange={set("badge")} className="input-editorial">
            {BADGES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <div className="sm:col-span-2 lg:col-span-3">
            <p className="text-[10px] uppercase tracking-widest text-[#71717A] mb-3">Photo du joueur *</p>
            <div className="flex flex-wrap items-center gap-5">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPhoto} className="hidden" data-testid="player-photo-input" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="player-photo-button"
                className="flex items-center gap-2 border border-[#27272A] px-5 py-3 text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors disabled:opacity-50"
              >
                <Upload size={14} /> {uploading ? "Envoi…" : form.image ? "Changer la photo" : "Téléverser la photo"}
              </button>
              {form.image && <img src={form.image} alt="Aperçu" className="h-20 w-16 object-cover border border-[#27272A]" data-testid="player-photo-preview" />}
            </div>
          </div>
          {["vitesse", "technique", "physique"].map((s) => (
            <div key={s}>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{s} ({form[s]})</label>
              <input data-testid={`player-input-${s}`} type="range" min="40" max="99" value={form[s]} onChange={set(s)} className="w-full accent-[#C5A059]" />
            </div>
          ))}
          <div className="sm:col-span-2 lg:col-span-3">
            <button type="submit" data-testid="player-submit-button" className="bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300">
              {editing ? "Mettre à jour" : "Ajouter le joueur"}
            </button>
          </div>
        </form>
      )}

      <div className="border border-[#27272A] divide-y divide-[#27272A]">
        {items.map((p) => (
          <div key={p.id} data-testid={`player-row-${p.id.slice(0, 8)}`} className="flex items-center gap-5 px-5 py-4 hover:bg-[#121214] transition-colors">
            <img src={p.image} alt={p.nom} className="w-12 h-12 object-cover grayscale" />
            <div className="flex-1 min-w-0">
              <p className="font-medium">{p.nom} <span className="text-[#71717A] font-normal">— {p.poste}, {p.age} ans</span></p>
              <p className="text-xs text-[#71717A]">{p.club} · {p.nationalite} · <span className="text-[#C5A059]">{p.badge}</span></p>
            </div>
            <button onClick={() => edit(p)} data-testid={`player-edit-${p.id.slice(0, 8)}`} className="text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Modifier"><Pencil size={15} /></button>
            <button onClick={() => remove(p.id)} data-testid={`player-delete-${p.id.slice(0, 8)}`} className="text-[#71717A] hover:text-red-400 transition-colors" aria-label="Supprimer"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
